import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * WEB-926, позиция 3 — график-прогноз (референс 12min: точка «Now» слева-внизу,
 * пунктирная кривая к бейджу-балуну «20 books per month» справа-вверху, подписи
 * дат под осью; оранжевый = цвет «будущего»).
 *
 * ЧТО ЭТО ЗА БЛОК. Обещание динамики, нарисованное ПО ПОДПИСЯМ АВТОРА. Он не
 * считает ничего по ответам пользователя и не претендует на точность: кривая
 * потому и пунктирная, что это прогноз, а не замер. Условия («при регулярных
 * занятиях») формулирует автор в подписи — мы за него ничего не утверждаем.
 *
 * Рисуем inline SVG без библиотек (как ChartBlockSvg, WEB-447). Отличие от
 * блока `chart`: там декоративная кривая с двумя значениями, здесь — именованная
 * точка старта, балун цели и подписи оси, то есть смысловые слоты автора.
 */

const VIEW_W = 320;
const VIEW_H = 180;

/** Якоря кривой. HTML-подписи позиционируются из них же — не разъедутся. */
const NOW_POINT = { x: 36, y: 140 };
const GOAL_POINT = { x: 284, y: 40 };

export type ForecastCurve = 'accelerating' | 'linear' | 'fast-start';

/**
 * Три честные формы одного обещания: разгон (медленно → быстро), ровный рост,
 * быстрый старт с выходом на плато. Значения совпадают с zod-схемой генерации.
 */
const CURVE_PATHS: Record<ForecastCurve, string> = {
  accelerating: `M ${NOW_POINT.x} ${NOW_POINT.y} C 130 138, 200 118, ${GOAL_POINT.x} ${GOAL_POINT.y}`,
  linear: `M ${NOW_POINT.x} ${NOW_POINT.y} L ${GOAL_POINT.x} ${GOAL_POINT.y}`,
  'fast-start': `M ${NOW_POINT.x} ${NOW_POINT.y} C 96 84, 170 54, ${GOAL_POINT.x} ${GOAL_POINT.y}`,
};

const GRID_LINES = [52, 92, 132];

function percent(value: number, total: number): string {
  return `${(value / total) * 100}%`;
}

interface ForecastChartBlockProps {
  title?: React.ReactNode;
  nowLabel?: string;
  nowValue?: string;
  goalLabel?: string;
  axisStartLabel?: string;
  axisEndLabel?: string;
  caption?: string;
  accentColor?: string;
  curve?: ForecastCurve;
  /** Локализованное название графика для скринридера (когда подписей нет). */
  chartLabel: string;
}

export function ForecastChartBlock({
  title,
  nowLabel,
  nowValue,
  goalLabel,
  axisStartLabel,
  axisEndLabel,
  caption,
  accentColor,
  curve,
  chartLabel,
}: ForecastChartBlockProps): React.ReactElement {
  const shape: ForecastCurve =
    curve === 'linear' || curve === 'fast-start' ? curve : 'accelerating';
  const accent = accentColor || 'var(--color-primary, #4094F7)';
  const nowColor = 'var(--color-text-primary, rgba(0,0,0,0.88))';
  const hasAxis = Boolean(axisStartLabel || axisEndLabel);

  // Одна осмысленная строка вместо озвучивания россыпи SVG-подписей.
  const ariaLabel = [chartLabel, nowLabel, nowValue, goalLabel].filter(Boolean).join(', ');

  // WEB-998: пузырь цели поднимается от точки на всю СВОЮ высоту (+20%
  // отступа), а точка цели стоит на ~22% высоты холста — многострочный пузырь
  // уходил в минус и ложился на заголовок блока. Меряем фактические высоты
  // (наблюдатель размера — паттерн WEB-983: поздняя отрисовка меняет меру) и
  // клэмпим верх пузыря нулём холста. Без меры (среда без наблюдателя) —
  // прежнее позиционирование.
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [clampedTop, setClampedTop] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const bubble = bubbleRef.current;
    if (!canvas || !bubble || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const applyMeasure = (): void => {
      const canvasH = canvas.offsetHeight;
      const bubbleH = bubble.offsetHeight;
      if (canvasH <= 0 || bubbleH <= 0) return;
      const anchorTop = canvasH * (GOAL_POINT.y / VIEW_H);
      // 1.2 = прежний сдвиг translateY(-120%): высота пузыря + 20% отступа.
      setClampedTop(Math.max(0, anchorTop - bubbleH * 1.2));
    };
    applyMeasure();
    const observer = new ResizeObserver(applyMeasure);
    observer.observe(bubble);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [goalLabel]);

  return (
    <div data-testid="forecast-chart" style={{ marginBottom: 16 }}>
      {title && (
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, textAlign: 'center' }}>
          {title}
        </div>
      )}
      <div ref={canvasRef} data-testid="forecast-chart-canvas" style={{ position: 'relative' }}>
        <svg
          data-testid="forecast-chart-svg"
          data-curve={shape}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={ariaLabel}
          style={{ display: 'block' }}
        >
          {GRID_LINES.map((y) => (
            <line
              key={y}
              x1={20}
              y1={y}
              x2={300}
              y2={y}
              stroke="var(--color-border-primary, #e8e8e8)"
              strokeWidth={1}
            />
          ))}
          <line
            x1={20}
            y1={158}
            x2={300}
            y2={158}
            stroke="var(--color-border-primary, #d9d9d9)"
            strokeWidth={1}
          />
          {/*
            Пунктир — не декор: сплошная линия читалась бы как измеренные данные,
            а это прогноз.
          */}
          <path
            data-testid="forecast-chart-curve"
            d={CURVE_PATHS[shape]}
            fill="none"
            stroke={accent}
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray="7 7"
          />
          <circle
            cx={NOW_POINT.x}
            cy={NOW_POINT.y}
            r={6}
            fill={nowColor}
            stroke="var(--color-bg-surface, #fff)"
            strokeWidth={2}
          />
          <circle cx={GOAL_POINT.x} cy={GOAL_POINT.y} r={5} fill={accent} />
        </svg>

        {(nowLabel || nowValue) && (
          <div
            data-testid="forecast-chart-now"
            style={{
              position: 'absolute',
              left: percent(NOW_POINT.x, VIEW_W),
              top: percent(NOW_POINT.y + 10, VIEW_H),
              maxWidth: '45%',
              fontSize: 12,
              lineHeight: 1.3,
              color: nowColor,
            }}
          >
            {nowLabel && <div style={{ fontWeight: 600 }}>{nowLabel}</div>}
            {nowValue && (
              <div style={{ color: 'var(--color-text-secondary, #595959)' }}>{nowValue}</div>
            )}
          </div>
        )}

        {goalLabel && (
          <div
            ref={bubbleRef}
            data-testid="forecast-chart-goal"
            style={{
              position: 'absolute',
              left: percent(GOAL_POINT.x, VIEW_W),
              // WEB-998: с мерой — верх в пикселях с клэмпом ≥ 0 (сдвиг по Y
              // уже учтён в мере), без меры — прежняя формула.
              top:
                clampedTop !== undefined
                  ? `${clampedTop}px`
                  : percent(GOAL_POINT.y, VIEW_H),
              transform:
                clampedTop !== undefined
                  ? 'translateX(-88%)'
                  : 'translate(-88%, -120%)',
              maxWidth: '62%',
              padding: '6px 12px',
              borderRadius: 'var(--radius-card, var(--border-radius-lg, 12px))',
              background: accent,
              color: 'var(--color-bg-primary, #fff)',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {goalLabel}
          </div>
        )}
      </div>

      {hasAxis && (
        <div
          data-testid="forecast-chart-axis"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            fontSize: 12,
            color: 'var(--color-text-secondary, #595959)',
          }}
        >
          <span>{axisStartLabel}</span>
          <span style={{ color: accent }}>{axisEndLabel}</span>
        </div>
      )}

      {caption && (
        <div
          style={{
            marginTop: 10,
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--color-text-tertiary, #8c8c8c)',
          }}
        >
          {caption}
        </div>
      )}
    </div>
  );
}
